import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@store/theme/design";
import every from "lodash/every";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesWithChildrenStyles {
    container?: GridContainerProps;
    parentCategoryNameLink?: LinkPresentationProps;
    parentCategoryNameLinkItem?: InjectableCss;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    categoryBlankImage?: InjectableCss;
    subCatsWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    loadingText?: TypographyProps;
}

export const categoryDetailSubCategoriesWithChildrenStyles: CategoryDetailSubCategoriesWithChildrenStyles = {
    container: {
        gap: 0,
        css: css`
            margin-bottom: 2.5em;
        `,
    },
    parentCategoryNameLinkItem: {
        css: css`
            margin-top: 1em;
            margin-bottom: 1em;
        `,
    },
    parentCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                text-align: center;
                width: 100%;
                word-wrap: break-word;
                margin-bottom: 0.25em;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: ${TYPOGRAPHY.size.larger};
                                font-weight: ${TYPOGRAPHY.weight.bold};
                            `,
                            css`
                                font-size: ${TYPOGRAPHY.size.xlarge};
                                font-weight: ${TYPOGRAPHY.weight.bold};
                            `,
                            css`
                                font-size: ${TYPOGRAPHY.size.xlarge};
                                font-weight: ${TYPOGRAPHY.weight.bold};
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    subCategoryItem: {
        width: [12, 12, 4, 3, 2],
        css: css`
            padding-bottom: 20px;
            border: 1px solid ${getColor("common.border")};
            margin-top: -1px !important;
            margin-left: -1px !important;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [4, 4, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
            justify-content: center;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 160px;
                width: 160px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                height: 80px;
                                width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [8, 8, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            align-items: center;
                        `,
                        css`
                            align-items: center;
                        `,
                        css`
                            justify-content: center;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subCategoryNameLink: {
        icon: {
            iconProps: {
                src: ChevronRight,
            },
            position: "right",
        },
        css: css`
            width: 100%;
            & svg {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                text-align: left;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    categoryBlankImage: {
        css: css`
            background: ${getColor("common.border")};
            height: 130px;
            width: 160px;
            margin: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            height: 60px;
                            width: 105px;
                            margin: 8px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    subCatsWrapper: {
        css: css`
            display: block;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    loadingText: {
        css: css`
            text-align: center;
            display: block;
        `,
    },
};

const styles = categoryDetailSubCategoriesWithChildrenStyles;

const CategoryDetailSubCategoriesWithChildren: React.FC<Props> = props => {
    if (!props.category || !props.category.subCategoryIds || props.category.subCategoryIds.length === 0) {
        return null;
    }

    const {
        fields: { showImages },
    } = props;

    return (
        <StyledWrapper>
            {props.category.subCategoryIds.map(subCategoryId => (
                <SubCategoryLink key={subCategoryId} categoryId={subCategoryId} showImages={showImages} />
            ))}
        </StyledWrapper>
    );
};

const SubCategoryLinkView = ({
    category,
    subCategoriesIds,
    state,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    const hotfixPath = (path: string) => {
        return path.replace("/Products/Products", "/Products");
    };
    if (!category) {
        return null;
    }
    const [subCategories, setSubCategories] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    React.useEffect(() => {
        // setTimeout to force a loader animation during the time where react would otherwise display a blank widget
        setTimeout(() => {
            if (subCategoriesIds) {
                const temp: any[] = [];
                for (const subCategoryId of subCategoriesIds) {
                    const subCategoryData = getCategoryState(state, subCategoryId).value;
                    temp.push(subCategoryData as any);
                }
                setSubCategories(temp as any);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        }, 1250);
    }, [subCategoriesIds]);

    return (
        <>
            {isLoading && (
                <>
                    <Link
                        href={hotfixPath(category.path)}
                        {...styles.parentCategoryNameLink}
                        data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                    >
                        {category.shortDescription}
                    </Link>
                    <Typography {...styles.loadingText}>... {translate("Filtering category")} ...</Typography>
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                </>
            )}
            {!isLoading &&
                (every(subCategories, { properties: { fitsOn: "False" } }) ? (
                    <></>
                ) : (
                    <StyledWrapper {...styles.parentCategoryNameLinkItem}>
                        <Link
                            href={hotfixPath(category.path)}
                            {...styles.parentCategoryNameLink}
                            data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                        >
                            {category.shortDescription}
                        </Link>

                        <GridContainer {...styles.container}>
                            {subCategories.map((childCategory: any, index: any) => {
                                if (!childCategory) {
                                    return;
                                }
                                if (childCategory.properties && childCategory.properties.fitsOn === "False") {
                                    return;
                                }
                                return (
                                    <GridItem key={childCategory.shortDescription} {...styles.subCategoryItem}>
                                        <GridContainer {...styles.innerContainer}>
                                            {showImages && (
                                                <GridItem {...styles.subCategoryImageItem}>
                                                    {childCategory.smallImagePath && (
                                                        <Clickable
                                                            href={hotfixPath(childCategory.path)}
                                                            {...styles.subCategoryImageClickable}
                                                        >
                                                            <LazyImage
                                                                src={childCategory.smallImagePath}
                                                                {...styles.subCategoryImage}
                                                            />
                                                        </Clickable>
                                                    )}
                                                    {!childCategory.smallImagePath && (
                                                        <StyledWrapper {...styles.categoryBlankImage}></StyledWrapper>
                                                    )}
                                                </GridItem>
                                            )}
                                            <GridItem {...styles.subCategoryNameLinkItem}>
                                                <Link
                                                    href={hotfixPath(childCategory.path)}
                                                    {...styles.subCategoryNameLink}
                                                    data-test-selector={`categoryDetailsSubCategoriesLink_${childCategory.id}`}
                                                >
                                                    {childCategory.shortDescription}
                                                </Link>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                );
                            })}
                        </GridContainer>
                    </StyledWrapper>
                ))}
        </>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => {
    const mainCategory = getCategoryState(state, ownProps.categoryId).value;
    const subCats = [];
    const subCategoryIds = mainCategory?.subCategoryIds;
    return {
        category: mainCategory,
        subCategoriesIds: subCategoryIds,
        state,
    };
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategoriesWithChildren),
    definition: {
        group: "Categories",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
